import { computed, shallowRef, watch, type Ref } from 'vue'
import { userCartoon, userInfo, userWork } from '@/api/home'
import type { UserCartoonItem, UserProfileInfo, UserWorkItem } from '@/api/home/type'
import { useUserStore } from '@/stores/user'

export type ProfileMainTab = 'works' | 'cartoons'
export type ProfileWorkFilter = 'all' | 'pipe' | 'media' | 'radio'
export type ProfileLoadError =
  | { type: 'login-required' }
  | { type: 'not-found' }
  | { type: 'network' }
  | { type: 'unknown'; code?: number; message: string }

export const PROFILE_WORK_FILTERS: { key: ProfileWorkFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'media', label: '视频' },
  { key: 'pipe', label: '互动' },
  { key: 'radio', label: '听听' },
]

const USER_PROFILE_EMPTY_PAGE_AUTOFILL_LIMIT = 3

function getProfileBusinessError(response: { code: number; msg?: string }): ProfileLoadError | null {
  if (response.code === 0) {
    return null
  }

  if (response.code === 302) {
    return { type: 'login-required' }
  }

  if (response.code === 400 && response.msg === '无效用户') {
    return { type: 'not-found' }
  }

  return {
    type: 'unknown',
    code: response.code,
    message: response.msg || '用户主页加载失败',
  }
}

export function useUserProfile(profileId: Ref<string>) {
  const userStore = useUserStore()
  const profile = shallowRef<UserProfileInfo | null>(null)
  const works = shallowRef<UserWorkItem[]>([])
  const cartoons = shallowRef<UserCartoonItem[]>([])
  const activeTab = shallowRef<ProfileMainTab>('works')
  const activeWorkFilter = shallowRef<ProfileWorkFilter>('all')
  const loadingProfile = shallowRef(false)
  const loadingWorks = shallowRef(false)
  const loadingMoreWorks = shallowRef(false)
  const loadingCartoons = shallowRef(false)
  const error = shallowRef<ProfileLoadError | null>(null)
  const worksHasNext = shallowRef(false)
  const worksPageNo = shallowRef(1)
  const worksSecPageNo = shallowRef(-1)
  const loadedCartoonsFor = shallowRef('')
  let profileRequestId = 0
  let worksRequestId = 0
  let cartoonsRequestId = 0

  const isLoggedIn = computed(() => userStore.isLoggedIn())
  const isInitialLoading = computed(() => loadingProfile.value || loadingWorks.value)
  const canLoadMoreWorks = computed(
    () =>
      activeTab.value === 'works' &&
      works.value.length > 0 &&
      worksHasNext.value &&
      !loadingWorks.value &&
      !loadingMoreWorks.value,
  )

  function setActiveTab(tab: ProfileMainTab) {
    activeTab.value = tab
  }

  function setActiveWorkFilter(filter: ProfileWorkFilter) {
    activeWorkFilter.value = filter
  }

  function resetLists() {
    works.value = []
    cartoons.value = []
    worksHasNext.value = false
    worksPageNo.value = 1
    worksSecPageNo.value = -1
    loadedCartoonsFor.value = ''
  }

  function setLoginRequired() {
    profile.value = null
    resetLists()
    userStore.resetUserInfo()
    error.value = { type: 'login-required' }
  }

  function applyBusinessError(nextError: ProfileLoadError) {
    if (nextError.type === 'login-required') {
      setLoginRequired()
      return
    }

    error.value = nextError
  }

  function canRequestProtectedProfile() {
    if (isLoggedIn.value) {
      return true
    }

    setLoginRequired()
    return false
  }

  async function refreshProfile() {
    const id = profileId.value
    const requestId = ++profileRequestId

    if (!canRequestProtectedProfile()) {
      profile.value = null
      loadingProfile.value = false
      return
    }

    if (!id) {
      error.value = { type: 'unknown', message: '缺少用户标识' }
      profile.value = null
      resetLists()
      return
    }

    loadingProfile.value = true
    error.value = null

    try {
      const res = await userInfo(id)

      if (requestId !== profileRequestId) {
        return
      }

      const businessError = getProfileBusinessError(res)

      if (businessError) {
        applyBusinessError(businessError)
        profile.value = null
        resetLists()
        return
      }

      profile.value = res.data
    } catch (err) {
      if (requestId !== profileRequestId) {
        return
      }

      error.value = { type: 'network' }
      profile.value = null
      resetLists()
    } finally {
      if (requestId === profileRequestId) {
        loadingProfile.value = false
      }
    }
  }

  async function refreshWorks() {
    const id = profileId.value
    const requestId = ++worksRequestId

    if (!canRequestProtectedProfile() || error.value?.type === 'not-found') {
      loadingWorks.value = false
      works.value = []
      worksHasNext.value = false
      return
    }

    if (!id) {
      works.value = []
      worksHasNext.value = false
      return
    }

    loadingWorks.value = true
    error.value = null
    works.value = []
    worksHasNext.value = false
    worksPageNo.value = 1
    worksSecPageNo.value = -1

    try {
      const res = await userWork(id, activeWorkFilter.value, -1, 1)

      if (requestId !== worksRequestId) {
        return
      }

      const businessError = getProfileBusinessError(res)

      if (businessError) {
        applyBusinessError(businessError)
        works.value = []
        worksHasNext.value = false
        return
      }

      const nextItems = res.data.list ?? []

      works.value = nextItems
      worksHasNext.value = nextItems.length > 0 && Boolean(res.data.hasNext)
      worksPageNo.value = res.data.pageNo ?? 1
      worksSecPageNo.value = res.data.secPageNo ?? -1
    } catch (err) {
      if (requestId !== worksRequestId) {
        return
      }

      error.value = { type: 'network' }
      works.value = []
    } finally {
      if (requestId === worksRequestId) {
        loadingWorks.value = false
      }
    }
  }

  async function loadMoreWorks() {
    if (!canRequestProtectedProfile() || !canLoadMoreWorks.value || !profileId.value) {
      return
    }

    const requestId = ++worksRequestId
    let nextPageNo = worksPageNo.value
    let nextSecPageNo = worksSecPageNo.value
    let emptyPageCount = 0

    loadingMoreWorks.value = true

    try {
      while (true) {
        const res = await userWork(
          profileId.value,
          activeWorkFilter.value,
          nextSecPageNo,
          nextPageNo,
        )

        if (requestId !== worksRequestId) {
          return
        }

        const businessError = getProfileBusinessError(res)

        if (businessError) {
          applyBusinessError(businessError)
          worksHasNext.value = false
          return
        }

        const existingIds = new Set(works.value.map((item) => item.id))
        const nextItems = (res.data.list ?? []).filter((item) => !existingIds.has(item.id))
        const responseNextPageNo = res.data.pageNo ?? nextPageNo
        const responseNextSecPageNo = res.data.secPageNo ?? nextSecPageNo
        const cursorAdvanced =
          responseNextPageNo !== nextPageNo || responseNextSecPageNo !== nextSecPageNo

        if (nextItems.length) {
          works.value = works.value.concat(nextItems)
        }

        worksHasNext.value = Boolean(res.data.hasNext)
        worksPageNo.value = responseNextPageNo
        worksSecPageNo.value = responseNextSecPageNo

        if (nextItems.length || !res.data.hasNext) {
          return
        }

        emptyPageCount += 1

        if (!cursorAdvanced || emptyPageCount >= USER_PROFILE_EMPTY_PAGE_AUTOFILL_LIMIT) {
          worksHasNext.value = false
          return
        }

        nextPageNo = responseNextPageNo
        nextSecPageNo = responseNextSecPageNo
      }
    } catch (err) {
      if (requestId === worksRequestId) {
        error.value = { type: 'network' }
      }
    } finally {
      if (requestId === worksRequestId) {
        loadingMoreWorks.value = false
      }
    }
  }

  async function refreshCartoons() {
    const id = profileId.value
    const requestId = ++cartoonsRequestId

    if (!canRequestProtectedProfile() || error.value?.type === 'not-found') {
      loadingCartoons.value = false
      cartoons.value = []
      return
    }

    if (!id) {
      cartoons.value = []
      return
    }

    loadingCartoons.value = true
    error.value = null

    try {
      const res = await userCartoon(id, 1)

      if (requestId !== cartoonsRequestId) {
        return
      }

      const businessError = getProfileBusinessError(res)

      if (businessError) {
        applyBusinessError(businessError)
        cartoons.value = []
        return
      }

      cartoons.value = res.data.list ?? []
      loadedCartoonsFor.value = id
    } catch (err) {
      if (requestId !== cartoonsRequestId) {
        return
      }

      error.value = { type: 'network' }
      cartoons.value = []
    } finally {
      if (requestId === cartoonsRequestId) {
        loadingCartoons.value = false
      }
    }
  }

  async function refresh() {
    loadedCartoonsFor.value = ''

    await refreshProfile()

    if (error.value) {
      return
    }

    await refreshWorks()

    if (activeTab.value === 'cartoons') {
      await refreshCartoons()
    }
  }

  watch([profileId, isLoggedIn], refresh, { immediate: true })
  watch(activeWorkFilter, refreshWorks)
  watch(activeTab, (tab) => {
    if (
      tab === 'cartoons' &&
      !error.value &&
      loadedCartoonsFor.value !== profileId.value
    ) {
      void refreshCartoons()
    }
  })

  return {
    profile,
    works,
    cartoons,
    activeTab,
    activeWorkFilter,
    loadingProfile,
    loadingWorks,
    loadingMoreWorks,
    loadingCartoons,
    isInitialLoading,
    canLoadMoreWorks,
    error,
    worksHasNext,
    setActiveTab,
    setActiveWorkFilter,
    refresh,
    refreshWorks,
    refreshCartoons,
    loadMoreWorks,
  }
}
